import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useEffect, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { StackScreenProps } from '@react-navigation/stack';

import { LoadingScreen } from './LoadingScreen';
import * as Actions from '../store/Actions';
import { User } from '../types/User';
import { log, logError } from '../utils/logger';
import * as userToken from '../utils/token';

type RootStackParamList = {
  ScreenZero: undefined;
  a: undefined;
  TermsPrivacy: undefined;
  UserDetails: undefined;
};

interface RootState {
  registration: {
    user: Partial<User>;
  };
}

const mapStateToProps = (state: RootState) => ({
  registration: state.registration,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators(Actions, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type ScreenZeroProps = PropsFromRedux & StackScreenProps<RootStackParamList, 'ScreenZero'>;

function ScreenZero({ navigation, registration, actions }: ScreenZeroProps) {
  const { user } = registration;
  const [userId, setUserId] = useState(user.id);

  useEffect(() => {
    setUserId(registration.user?.id);
  }, [registration.user]);

  useEffect(() => {
    (async () => {
      const logTag = '[ScreenZero.useEffect(), prompt to complete profile decider]';
      const isExtantUser = await (async () => {
        try {
          return Boolean(await userToken.getToken());
        } catch (error) {
          logError(`${logTag} error retrieving user token: ${error}`);
          // Treat token retrieval error as no token scenario
          return false;
        }
      })();

      if (!isExtantUser) {
        /*
         * If no token exists in storage, we can't and won't know anything about the user until they either sign in, or
         * go through the registration process.  We delegate to those contexts to prompt for what ever information is
         * needed as part of those flows.
         *
         * Crucially, to avoid getting in the way of the registration flow as updates to the user are received as the
         * flow progresses, we intentionally REPLACE ourselves with the target screen; we are unmounted, ensuring that
         * any effects we have registered will not fire, and will not race with navigation during any other flows.
         */

        log(`${logTag} no user token; _replacing_ to main app`);
        navigation.replace('a');
        return;
      } else if (!userId) {
        /*
         * If we have a token - but no userID (from the above check) - this describes the situation when we've just
         * booted, and are still waiting for the initial fetch of user information from the backend.  In this case, we
         * must remain mounted to be able to identify any missing information when the user's profile information is
         * received.
         *
         * In this case we simply navigate to the initial usable screen, and remain ready to respond when the user data
         * changes.
         */

        log(`${logTag} no user id; _navigating_ to main app`);
        navigation.navigate('a');
        return;
      }

      /*
       * The main purposes of this screen is to prompt the user for any (optional) information that may have been
       * omitted during registration, or if terms and conditions have changed that need to be accepted again. This
       * happens only ONCE per app session/boot, so once we've determine what action is needed, we intentionally REPLAXE
       * ourselves to umount and avoid unintentional navigation intefering with other flows.
       */

      function navigateToDefaultRoute() {
        actions.refreshRedux();
        navigation.replace('a');
      }

      if (user.docs && user.docs.length > 0) {
        navigation.replace('TermsPrivacy');
      } else if (
        !user.onboarding_complete ||
        !user.first_name ||
        !user.last_name ||
        !Number.isInteger(user.birth_date) ||
        !user.zip_code
      ) {
        navigation.replace('UserDetails');
      } else {
        AsyncStorage.setItem('onboarded', '1').then(navigateToDefaultRoute).catch(navigateToDefaultRoute);
        // Always navigate to the default route, even if an error occurs (and is surpressed) when trying to access the
        // AsyncStorage.
      }
    })();
  }, [userId, navigation, actions]);

  return <LoadingScreen />;
}

export default connector(ScreenZero);
